import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Select, 
  Tag, 
  Space, 
  Typography, 
  Row, 
  Col,
  Spin,
  App,
  Tabs,
  Upload,
  Avatar,
  List,
  Alert,
  Image
} from 'antd';
import { 
  SaveOutlined, 
  ArrowLeftOutlined, 
  UserOutlined,
  UploadOutlined,
  PlusOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  LinkOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { contactApi, broadcastGroupApi, hashtagApi } from '../services/contactApi';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import useAuthUploadSrc from '../hooks/useAuthUploadSrc';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_URL || '';
const { TextArea } = Input;
const { Option } = Select;

const ContactEditAvatar = ({ url, size = 64 }) => {
  const src = useAuthUploadSrc(url);
  return <Avatar size={size} src={src} icon={<UserOutlined />} />;
};

const isImageUrl = (url) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url || '');

const AttachmentThumbnail = ({ url }) => {
  const src = useAuthUploadSrc(url);
  if (!isImageUrl(url)) {
    return (
      <div
        style={{
          width: 60,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff1f0',
          borderRadius: 6,
          border: '1px solid #ffccc7',
        }}
      >
        <FilePdfOutlined style={{ fontSize: 28, color: '#ff4d4f' }} />
      </div>
    );
  }
  return (
    <Image
      src={src}
      width={60}
      height={60}
      style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #f0f0f0', display: 'block' }}
      preview={{ src }}
      placeholder={
        <div
          style={{
            width: 60,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fafafa',
            borderRadius: 6,
          }}
        >
          <FileImageOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
        </div>
      }
    />
  );
};

/** 頭像 + 右下角 + 作為上傳觸發（不另設按鈕） */
const ContactAvatarUploadTrigger = ({
  url,
  size = 88,
  uploading,
  disabled,
  customRequest,
}) => (
  <Upload
    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
    showUploadList={false}
    disabled={disabled || uploading}
    customRequest={customRequest}
  >
    <div
      role="button"
      tabIndex={0}
      style={{
        position: 'relative',
        display: 'inline-block',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.65 : 1,
      }}
    >
      <ContactEditAvatar url={url} size={size} />
      <span
        style={{
          position: 'absolute',
          right: -2,
          bottom: -2,
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: '#fff',
          border: '1px solid #e8e8e8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
          pointerEvents: 'none',
        }}
      >
        {uploading ? (
          <Spin size="small" />
        ) : (
          <PlusOutlined style={{ fontSize: 15, color: '#595959' }} />
        )}
      </span>
    </div>
  </Upload>
);

const resolveFetchUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path}`;
};

const ContactEditPageContent = () => {
  console.log('🚀 ContactEditPageContent - Component initialized');
  
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useLanguage();
  const isEdit = !!id;
  
  console.log('🔧 ContactEditPageContent - Params:', { id, isEdit });
  
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { message } = App.useApp();
  
  // 選項數據
  const [groups, setGroups] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [availableHashtags, setAvailableHashtags] = useState([]);
  
  // 表單數據
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    occupation: '',
    whatsapp_number: '',
    email: '',
    company_name: '',
    department: '',
    position: '',
    hashtags: '',
    broadcast_group_id: '',
    is_active: true
  });

  const [avatarUrl, setAvatarUrl] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  // 載入選項數據
  const loadOptions = async () => {
    console.log('📋 ContactEditPageContent - Loading options data');
    try {
      const [groupsResponse, hashtagsResponse] = await Promise.all([
        broadcastGroupApi.getGroups(),
        hashtagApi.getHashtags()
      ]);
      console.log('📋 ContactEditPageContent - Groups response:', groupsResponse);
      console.log('📋 ContactEditPageContent - Hashtags response:', hashtagsResponse);
      
      // 確保 groups 是數組
      const groupsData = Array.isArray(groupsResponse) ? groupsResponse : 
                        (groupsResponse?.data && Array.isArray(groupsResponse.data)) ? groupsResponse.data : [];
      
      // 確保 hashtags 是數組
      const hashtagsData = Array.isArray(hashtagsResponse) ? hashtagsResponse : 
                          (hashtagsResponse?.data && Array.isArray(hashtagsResponse.data)) ? hashtagsResponse.data : [];
      
      setGroups(groupsData);
      setHashtags(hashtagsData);
      setAvailableHashtags(hashtagsData);
      
      console.log('📋 ContactEditPageContent - Options loaded successfully');
    } catch (err) {
      console.error('載入選項數據失敗：', err);
      // 設置默認空數組
      setGroups([]);
      setHashtags([]);
      setAvailableHashtags([]);
    }
  };

  // 載入聯絡人數據
  const loadContact = async () => {
    if (!isEdit) return;
    
    setLoading(true);
    try {
      console.log('📥 ContactEditPage - Loading contact with ID:', id);
      const contact = await contactApi.getContact(id);
      console.log('📥 ContactEditPage - Loaded contact data:', contact);
      console.log('📱 ContactEditPage - Loaded whatsapp_number:', contact.whatsapp_number);
      console.log('📱 ContactEditPage - Loaded whatsapp_number type:', typeof contact.whatsapp_number);
      
      setFormData(contact);
      form.setFieldsValue(contact);
      setAvatarUrl(contact.avatar_url || '');
      setAttachments(Array.isArray(contact.attachments) ? contact.attachments : []);
      
      // 檢查表單欄位是否正確設置
      const formValues = form.getFieldsValue();
      console.log('📋 ContactEditPage - Form values after setting:', formValues);
      console.log('📱 ContactEditPage - Form whatsapp_number:', formValues.whatsapp_number);
    } catch (err) {
      console.error('ContactEditPage - Load contact error:', err);
      message.error(t('contactList.loadError') + ': ' + (err.response?.data || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 初始載入
  useEffect(() => {
    console.log('🔥 ContactEditPageContent - useEffect triggered');
    console.log('🔥 ContactEditPageContent - useEffect dependencies:', { id, isEdit });
    
    loadOptions();
    if (isEdit) {
      console.log('🔥 ContactEditPageContent - Loading contact for edit mode');
      loadContact();
    } else {
      console.log('🔥 ContactEditPageContent - Creating new contact');
    }
  }, [id]);

  // 表單提交
  const handleSubmit = async (values) => {
    console.log('🚀 ContactEditPage - Form submit started');
    console.log('📋 ContactEditPage - Form values:', values);
    console.log('📋 ContactEditPage - Form values JSON:', JSON.stringify(values, null, 2));
    console.log('🔧 ContactEditPage - IsEdit:', isEdit);
    console.log('🔧 ContactEditPage - ID:', id);
    
    // 特別檢查 WhatsApp 號碼欄位
    console.log('📱 ContactEditPage - whatsapp_number value:', values.whatsapp_number);
    console.log('📱 ContactEditPage - whatsapp_number type:', typeof values.whatsapp_number);
    console.log('📱 ContactEditPage - whatsapp_number length:', values.whatsapp_number?.length);
    console.log('📱 ContactEditPage - whatsapp_number is empty:', !values.whatsapp_number);
    console.log('📱 ContactEditPage - whatsapp_number is null:', values.whatsapp_number === null);
    console.log('📱 ContactEditPage - whatsapp_number is undefined:', values.whatsapp_number === undefined);
    
    // 特別檢查廣播群組欄位
    console.log('📡 ContactEditPage - broadcast_group_id value:', values.broadcast_group_id);
    console.log('📡 ContactEditPage - broadcast_group_id type:', typeof values.broadcast_group_id);
    console.log('📡 ContactEditPage - broadcast_group_id is empty:', !values.broadcast_group_id);
    console.log('📡 ContactEditPage - broadcast_group_id is null:', values.broadcast_group_id === null);
    console.log('📡 ContactEditPage - broadcast_group_id is undefined:', values.broadcast_group_id === undefined);
    
    // 清理空字串欄位，轉換為 null 或 undefined
    const cleanedValues = {
      ...values,
      whatsapp_number: values.whatsapp_number?.trim() || '',
      title: values.title?.trim() || null,
      occupation: values.occupation?.trim() || null,
      email: values.email?.trim() || null,
      company_name: values.company_name?.trim() || null,
      department: values.department?.trim() || null,
      position: values.position?.trim() || null,
      hashtags: values.hashtags?.trim() || null,
      avatar_url: avatarUrl?.trim() ? avatarUrl.trim() : null,
      attachments,
    };
    
    setSaving(true);
    try {
      if (isEdit) {
        console.log('📤 ContactEditPage - Calling updateContact with ID:', id);
        console.log('📤 ContactEditPage - Update data being sent:', JSON.stringify(cleanedValues, null, 2));
        const updateResult = await contactApi.updateContact(id, cleanedValues);
        console.log('✅ ContactEditPage - Update result:', updateResult);
        message.success(t('contactList.updateSuccess'));
      } else {
        console.log('📤 ContactEditPage - Calling createContact');
        console.log('📤 ContactEditPage - Create data being sent:', JSON.stringify(cleanedValues, null, 2));
        const createResult = await contactApi.createContact(cleanedValues);
        console.log('✅ ContactEditPage - Create result:', createResult);
        message.success(t('contactList.createSuccess'));
      }
      navigate('/contacts');
    } catch (err) {
      console.error('ContactEditPage - Submit error:', err);
      console.error('Error response:', err.response?.data);
      
      let errorMessage = err.response?.data || err.message;
      if (err.response?.data?.errors) {
        // 處理驗證錯誤
        console.error('Validation errors:', err.response.data.errors);
        const validationErrors = Object.values(err.response.data.errors).flat();
        console.error('Flattened validation errors:', validationErrors);
        errorMessage = validationErrors.join(', ');
      }
      
      message.error((isEdit ? t('contactList.updateError') : t('contactList.createError')) + ': ' + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // 添加標籤
  const handleAddHashtag = (hashtag) => {
    const currentValue = form.getFieldValue('hashtags') || '';
    const currentTags = currentValue ? currentValue.split(',').map(t => t.trim()) : [];
    if (!currentTags.includes(hashtag)) {
      const newTags = [...currentTags, hashtag].join(', ');
      
      // 同時更新 Form 值和 formData 狀態
      form.setFieldValue('hashtags', newTags);
      setFormData(prev => ({ ...prev, hashtags: newTags }));
    }
  };

  // 移除標籤
  const handleRemoveHashtag = (hashtag) => {
    const currentValue = form.getFieldValue('hashtags') || '';
    const currentTags = currentValue ? currentValue.split(',').map(t => t.trim()) : [];
    const newTags = currentTags.filter(tag => tag !== hashtag).join(', ');
    
    // 同時更新 Form 值和 formData 狀態
    form.setFieldValue('hashtags', newTags);
    setFormData(prev => ({ ...prev, hashtags: newTags }));
  };

  // 格式化標籤顯示
  const formatHashtags = (hashtags) => {
    if (!hashtags) return [];
    return hashtags.split(',').map(tag => tag.trim()).filter(tag => tag);
  };

  const openProtectedUpload = async (path) => {
    try {
      const token = localStorage.getItem('token');
      const fetchUrl = resolveFetchUrl(path);
      const res = await fetch(fetchUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      window.open(u, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(u), 120000);
    } catch (e) {
      console.error(e);
      message.error(t('contactList.uploadError'));
    }
  };

  const handleRemoveAttachment = async (item) => {
    if (!isEdit || !id) return;
    try {
      await contactApi.deleteContactAttachment(id, item.url);
      setAttachments((prev) => prev.filter((a) => a.url !== item.url));
      message.success(t('contactList.removeSuccess'));
    } catch (err) {
      console.error(err);
      message.error(t('contactList.removeError'));
    }
  };

  const handleAvatarCustomRequest = async ({ file, onSuccess, onError }) => {
    if (!isEdit || !id) {
      message.warning(t('contactList.saveBeforeUpload'));
      if (onError) onError();
      return;
    }
    setAvatarUploading(true);
    try {
      const res = await contactApi.uploadContactAvatar(id, file);
      if (res.success && res.url) {
        setAvatarUrl(res.url);
        message.success(t('contactList.uploadSuccess'));
        if (onSuccess) onSuccess(res);
      } else {
        message.error(res.message || t('contactList.uploadError'));
        if (onError) onError();
      }
    } catch (e) {
      message.error(t('contactList.uploadError'));
      if (onError) onError(e);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAttachmentCustomRequest = async ({ file, onSuccess, onError }) => {
    if (!isEdit || !id) {
      message.warning(t('contactList.saveBeforeUpload'));
      if (onError) onError();
      return;
    }
    setAttachmentUploading(true);
    try {
      const res = await contactApi.uploadContactAttachment(id, file);
      if (res.success && res.attachment) {
        setAttachments((prev) => [...prev, res.attachment]);
        message.success(t('contactList.uploadSuccess'));
        if (onSuccess) onSuccess(res);
      } else {
        message.error(t('contactList.uploadError'));
        if (onError) onError();
      }
    } catch (e) {
      message.error(t('contactList.uploadError'));
      if (onError) onError(e);
    } finally {
      setAttachmentUploading(false);
    }
  };

  console.log('🎨 ContactEditPageContent - Rendering component');
  console.log('🎨 ContactEditPageContent - Current state:', { loading, saving, isEdit, id });
  console.log('🎨 ContactEditPageContent - Form data:', formData);
  
  return (
    <div style={{ padding: '24px' }}>
      <Spin spinning={loading}>
        {/* 頁面標題 */}
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Button 
              type="primary"
              shape="square"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/contacts')}
              style={{ 
                width: '40px', 
                height: '40px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </Col>
          <Col style={{ textAlign: 'right' }}>
            <Title level={2} style={{ margin: 0 }}>
              {t('contactList.management')}
            </Title>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={16}>
            {/* 聯絡人表單 */}
            <Card>
              <Tabs
                defaultActiveKey="basic"
                destroyInactiveTabPane={false}
                items={[
                  {
                    key: 'basic',
                    label: t('contactList.tabBasic'),
                    children: (
              <Form
                form={form}
                layout="vertical"
                onFinish={(values) => {
                  console.log('📝 ContactEditPageContent - Form onFinish triggered');
                  console.log('📝 ContactEditPageContent - Form values:', values);
                  console.log('📝 ContactEditPageContent - Form values keys:', Object.keys(values));
                  console.log('📝 ContactEditPageContent - broadcast_group_id in values:', values.broadcast_group_id);
                  console.log('📝 ContactEditPageContent - broadcast_group_id type:', typeof values.broadcast_group_id);
                  handleSubmit(values);
                }}
                onFinishFailed={(errorInfo) => {
                  console.error('❌ ContactEditPageContent - Form validation failed:', errorInfo);
                  message.error(t('contactList.formValidationFailed'));
                }}
                initialValues={formData}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label={t('contactList.name')}
                      rules={[{ required: true, message: t('contactList.nameRequired') }]}
                    >
                      <Input 
                        placeholder={t('contactList.namePlaceholder')}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, name: e.target.value }));
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="title"
                      label={t('contactList.title')}
                    >
                      <Input 
                        placeholder={t('contactList.titlePlaceholder')}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, title: e.target.value }));
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="occupation"
                      label={t('contactList.occupation')}
                    >
                      <Input 
                        placeholder={t('contactList.occupationPlaceholder')}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, occupation: e.target.value }));
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="position"
                      label={t('contactList.position')}
                    >
                      <Input 
                        placeholder={t('contactList.positionPlaceholder')}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, position: e.target.value }));
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="whatsapp_number"
                      label={t('contactList.whatsappNumber')}
                      rules={[{ required: true, message: t('contactList.whatsappNumberRequired') }]}
                    >
                      <Input 
                        placeholder={t('contactList.whatsappPlaceholder')}
                        onChange={(e) => {
                          console.log('📱 ContactEditPage - whatsapp_number input changed:', e.target.value);
                          console.log('📱 ContactEditPage - Input event:', e);
                          setFormData(prev => ({ ...prev, whatsapp_number: e.target.value }));
                        }}
                        onBlur={(e) => {
                          console.log('📱 ContactEditPage - whatsapp_number input blurred:', e.target.value);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="email"
                      label={t('contactList.email')}
                    >
                      <Input 
                        placeholder={t('contactList.emailPlaceholder')}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, email: e.target.value }));
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="company_name"
                      label={t('contactList.companyName')}
                    >
                      <Input 
                        placeholder={t('contactList.companyPlaceholder')}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, company_name: e.target.value }));
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="department"
                      label={t('contactList.department')}
                    >
                      <Input 
                        placeholder={t('contactList.departmentPlaceholder')}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, department: e.target.value }));
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="broadcast_group_id"
                  label={t('contactList.broadcastGroup')}
                  rules={[{ required: true, message: t('contactList.broadcastGroupRequired') }]}
                >
                  <Select 
                    placeholder={t('contactList.broadcastGroupPlaceholder')} 
                    allowClear
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, broadcast_group_id: value }));
                    }}
                  >
                    {Array.isArray(groups) && groups.map(group => (
                      <Option key={group.id} value={group.id}>
                        {group.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="hashtags"
                  label={t('contactList.hashtags')}
                >
                  <Input 
                    placeholder={t('contactList.hashtagsPlaceholder')}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, hashtags: e.target.value }));
                    }}
                  />
                </Form.Item>

                {/* 標籤顯示 */}
                {(() => {
                  const currentHashtags = form.getFieldValue('hashtags') || '';
                  const tags = formatHashtags(currentHashtags);
                  return tags.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <Text type='secondary'>{t('contactList.currentTags')}</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Space wrap>
                          {tags.map((tag) => (
                            <Tag
                              key={tag}
                              closable
                              onClose={() => handleRemoveHashtag(tag)}
                              color="blue"
                            >
                              #{tag}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    </div>
                  );
                })()}

                {/* 隱藏欄位 */}
                <Form.Item name="is_active" hidden>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button 
                      type="primary" 
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={saving}
                    >
                      {isEdit ? t('contactList.update') : t('contactList.create')}
                    </Button>
                    <Button onClick={() => navigate('/contacts')}>
                      {t('contactList.cancel')}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
                    )
                  },
                  {
                    key: 'attachments',
                    label: t('contactList.tabAttachments'),
                    children: (
                      <div>
                        {!isEdit && (
                          <Alert
                            type="info"
                            showIcon
                            message={t('contactList.saveBeforeUpload')}
                            style={{ marginBottom: 16 }}
                          />
                        )}
                        <Upload.Dragger
                          accept=".pdf,.png,.jpg,.jpeg,image/png,image/jpeg,application/pdf"
                          showUploadList={false}
                          multiple
                          disabled={!isEdit || attachmentUploading}
                          customRequest={handleAttachmentCustomRequest}
                          style={{ marginBottom: 8 }}
                        >
                          <p className="ant-upload-drag-icon">
                            {attachmentUploading
                              ? <Spin />
                              : <InboxOutlined style={{ fontSize: 36, color: '#1677ff' }} />
                            }
                          </p>
                          <p className="ant-upload-text">
                            {t('contactList.attachmentHint')}
                          </p>
                          <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                            {t('contactList.docTypesHint')}
                          </p>
                        </Upload.Dragger>
                        <List
                          style={{ marginTop: 24 }}
                          bordered
                          dataSource={attachments}
                          locale={{ emptyText: '—' }}
                          renderItem={(item) => (
                            <List.Item
                              actions={[
                                <Button
                                  key="open"
                                  type="link"
                                  icon={<LinkOutlined />}
                                  onClick={() => openProtectedUpload(item.url)}
                                >
                                  {t('contactList.openFile')}
                                </Button>,
                                <Button
                                  key="del"
                                  type="link"
                                  danger
                                  disabled={!isEdit}
                                  onClick={() => handleRemoveAttachment(item)}
                                >
                                  {t('contactList.removeFile')}
                                </Button>,
                              ]}
                            >
                              <List.Item.Meta
                                avatar={<AttachmentThumbnail url={item.url} />}
                                title={
                                  <span style={{ verticalAlign: 'middle' }}>
                                    {item.fileName || item.url}
                                  </span>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      </div>
                    ),
                  },
                ]}
              />
            </Card>
          </Col>

          <Col span={8}>
            <Card title={t('contactList.contactAvatar')} size="small">
              <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 8px' }}>
                <ContactAvatarUploadTrigger
                  url={avatarUrl}
                  size={88}
                  uploading={avatarUploading}
                  disabled={!isEdit}
                  customRequest={handleAvatarCustomRequest}
                />
              </div>
            </Card>

            {/* 快速添加標籤 */}
            <Card title={t('contactList.quickAddTags')} size='small' style={{ marginTop: 16 }}>
              <Space wrap>
                {Array.isArray(availableHashtags) && availableHashtags.map(hashtag => (
                  <Tag
                    key={hashtag.id}
                    color={hashtag.color || 'blue'}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleAddHashtag(hashtag.name)}
                  >
                    #{hashtag.name}
                  </Tag>
                ))}
              </Space>
            </Card>

            {/* 預覽 */}
            <Card title={t('contactList.preview')} size='small' style={{ marginTop: '16px' }}>
              <div>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong>{formData.name || t('contactList.name')}</Text>
                  {formData.title && (
                    <Text type="secondary" style={{ marginLeft: '8px' }}>
                      {formData.title}
                    </Text>
                  )}
                </div>
                
                {formData.whatsapp_number && (
                  <div style={{ marginBottom: '4px' }}>
                    <Text type="secondary">📱 {formData.whatsapp_number}</Text>
                  </div>
                )}
                
                {formData.email && (
                  <div style={{ marginBottom: '4px' }}>
                    <Text type="secondary">✉️ {formData.email}</Text>
                  </div>
                )}
                
                {formData.company_name && (
                  <div style={{ marginBottom: '4px' }}>
                    <Text type="secondary">🏢 {formData.company_name}</Text>
                  </div>
                )}
                
                {formData.department && (
                  <div style={{ marginBottom: '4px' }}>
                    <Text type="secondary">🏬 {formData.department}</Text>
                  </div>
                )}
                
                {formatHashtags(formData.hashtags).length > 0 && (
                  <div>
                    <Text type='secondary'>{t('contactList.tagsLabel')}</Text>
                    <div style={{ marginTop: '4px' }}>
                      <Space wrap>
                        {formatHashtags(formData.hashtags).map((tag) => (
                          <Tag key={tag} color="blue">#{tag}</Tag>
                        ))}
                      </Space>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

const ContactEditPage = () => {
  return (
    <App>
      <ContactEditPageContent />
    </App>
  );
};

export default ContactEditPage;